import { useState } from "react";

import { Icon } from "@blueprintjs/core";
import { Editor } from "@tiptap/react";
import { ChevronDown, LayoutTemplate, Mic, Redo2, Undo2 } from "lucide-react";

import { useScenarioTemplates } from "@/components/helpers/custom-hooks/use-scenario-templates";
import {
  getString,
  removeItem,
  setString,
} from "@/components/helpers/storage/local-storage-utils";
import { convertMarkdownToHtml } from "@/components/helpers/string-utils";
import { Flex } from "@/components/layout/flexbox";
import { ScenarioContexts } from "@/components/pages_logged_in/roleplays/details/create/ScenarioContexts";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ExpandableTextInput } from "@/components/ui/expandable-text-input";
import { WorkspaceContextFragment } from "@/graphql";

// Minimum character requirement for scenario input
const MIN_CHARACTERS = 100;

export function CreateScenarioInput({
  value,
  onChange,
  onCreate,
  onVoiceChat,
  loading,
}: {
  value: string;
  onChange: (value: string) => void;
  onCreate: (
    template?: { id: string; name: string; content: string },
    workspaceContextIds?: string[],
  ) => void;
  onVoiceChat: () => void;
  loading: boolean;
}) {
  const [attachedContexts, setAttachedContexts] = useState<
    WorkspaceContextFragment[]
  >([]);
  const { templatesByCategory, templatesById } = useScenarioTemplates();
  const [editor, setEditor] = useState<Editor | null>(null);
  const selectedTemplate = templatesById[editor?.$doc.attributes.templateId];

  // Calculate character count
  const characterCount = value.length;
  const remainingCharacters = MIN_CHARACTERS - characterCount;
  const isBelowMinimum = remainingCharacters > 0;

  // Get counter color based on character count
  const getCounterColor = () => {
    if (isBelowMinimum) return "text-gray-500";
    return "text-green-500";
  };

  return (
    <Card className="min-h-0 w-full max-w-4xl flex-auto gap-4 p-4">
      <CardHeader className="flex flex-wrap justify-between p-0">
        <Flex className="flex-wrap gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button intent="secondary" variant="outline" size="sm">
                <LayoutTemplate />
                {selectedTemplate ? selectedTemplate.name : "Use Template"}
                <ChevronDown />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              style={{ minWidth: "var(--radix-dropdown-menu-trigger-width)" }}
            >
              {Object.entries(templatesByCategory).map(
                ([category, templates]) => (
                  <DropdownMenuSub key={category}>
                    <DropdownMenuSubTrigger>{category}</DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      {templates.map((template) => (
                        <DropdownMenuItem
                          key={template.id}
                          onClick={() => {
                            const html = convertMarkdownToHtml(
                              template.content,
                            );
                            onChange(html);
                            setString("scenarioPromptTemplateId", template.id);
                            editor
                              ?.chain()
                              .setTemplateId(template.id)
                              .setContent(html)
                              .run();
                          }}
                        >
                          {template.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                ),
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <ScenarioContexts onChange={setAttachedContexts} />
        </Flex>
        <Button variant="outline" size="sm" onClick={onVoiceChat}>
          <Mic />
          Voice Setup
        </Button>
      </CardHeader>
      <hr className="m-0" />
      <CardContent
        className="-mt-4 flex flex-auto flex-col overflow-y-auto p-0"
        onClick={() => editor?.commands.focus()}
      >
        <div className="from-background sticky top-0 z-10 min-h-6 w-full bg-linear-to-b to-transparent" />
        <div className="-mt-2">
          <ExpandableTextInput
            onEditorChange={setEditor}
            value={value}
            onChange={onChange}
            placeholder="Practice delivering feedback to a peer who has missed three project deadlines and interrupted team members during client presentations."
            templateId={getString("scenarioPromptTemplateId")}
            className="px-2"
          />
        </div>

        <div className="from-background sticky bottom-0 z-10 min-h-6 w-full bg-linear-to-t to-transparent" />
      </CardContent>
      <CardFooter className="flex flex-wrap justify-between gap-2 p-0">
        <div>
          {/* Character counter */}
          <div className="py-1">
            <div className={`text-xs ${getCounterColor()}`}>
              {characterCount} / {MIN_CHARACTERS} characters
              {isBelowMinimum && (
                <span className="ml-2 text-gray-500">
                  ({remainingCharacters} more required)
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              intent="secondary"
              variant="outline"
              size="sm"
              onClick={() => {
                removeItem("scenarioPrompt");
                onChange("");
                editor?.chain().setTemplateId("").setContent("").run();
                removeItem("scenarioPromptTemplateId");
              }}
            >
              Clear
            </Button>
            <Button
              intent="secondary"
              variant="outline"
              size="sm"
              onClick={() => {
                editor?.chain().focus().undo().run();
                const templateId = editor?.state.doc.attrs.templateId;
                if (templateId) {
                  setString("scenarioPromptTemplateId", templateId);
                } else {
                  removeItem("scenarioPromptTemplateId");
                }
              }}
              disabled={!editor?.can().undo()}
            >
              <Undo2 />
            </Button>
            <Button
              intent="secondary"
              variant="outline"
              size="sm"
              onClick={() => {
                editor?.chain().focus().redo().run();
                const templateId = editor?.state.doc.attrs.templateId;
                if (templateId) {
                  setString("scenarioPromptTemplateId", templateId);
                } else {
                  removeItem("scenarioPromptTemplateId");
                }
              }}
              disabled={!editor?.can().redo()}
            >
              <Redo2 />
            </Button>
          </div>
        </div>

        <Button
          intent="primary"
          size="sm"
          onClick={() => {
            onCreate(
              selectedTemplate,
              attachedContexts.map((context) => context.id),
            );
          }}
          disabled={loading || isBelowMinimum}
        >
          <Icon icon="clean" color="white" />
          Create
        </Button>
      </CardFooter>
    </Card>
  );
}
